import { cifValidation, nieValidation, nifValidation } from '@ctn/validation-util';
import { yupResolver } from '@hookform/resolvers/yup';
import { type Resolver } from 'react-hook-form';
import * as yup from 'yup';

import ERROR_MESSAGE, { ERROR_TYPE } from '@/constants/errors.constants';
import { REGEX_NUMBER } from '@/constants/regex';
import { ID_PAIS, TIPO_DIRECCION, TIPO_DOCUMENTO_IDENT, TIPO_PERSONA } from '@/constants/tramite.constants';
import { getAllSujetos } from '@/helpers/formSolicitudes';
import { documentoLimitadoSchema } from '@/helpers/validationsDocuments';
import {
	type FormValues,
	type IDireccion,
	type IDocumentoForm,
	type IRepresentante,
	type ISujetoListado,
} from '@/interfaces/model/don.interface';

export const classNameComponent = 'c-pnp-interviniente';

export type DireccionForm = Omit<IDireccion, 'tipoDireccion'> & {
	idTipoDireccion: string;
};
export type SujetoForm = Omit<
ISujetoListado,
'tipoIntervencion' | 'direccion' | 'tipoDocumentoIdent' | 'tipoPersona' | 'id'
> & {
	id?: number;
	idReact?: string;
	idTipoPersona: string;
	idTipoDocumentoIdent: string;
	idTipoIntervencion: string;
	direccion: DireccionForm;
	documentoPoder?: IDocumentoForm;
};

export type RepresentanteForm = Omit<
ISujetoListado,
'tipoIntervencion' | 'direccion' | 'tipoDocumentoIdent' | 'tipoPersona' | 'id'
> & {
	id?: number;
	idReact?: string;
	idTipoDocumentoIdent: string;
	documentoPoder: IDocumentoForm;
	direccion: DireccionForm;
};

type getDefaultValuesParams = { idTipoIntervencion?: string } | undefined;

export const getDefaultValues = (params: getDefaultValuesParams = {}): SujetoForm => {
	const { idTipoIntervencion = '' } = params;
	return {
		idTipoPersona: '',
		idTipoDocumentoIdent: '',
		idTipoIntervencion,
		telefonoNumero: '',
		telefonoCodigoPais: '',
		numeroDocumento: '',
		nombre: '',
		apellido1RacSoc: '',
		apellido2: '',
		nacionalidad: ID_PAIS.ESPANA,
		email: '',
		documentoPoder: undefined,
		direccion: {
			idTipoDireccion: String(TIPO_DIRECCION.SIN_DIRECCION),
			codProvincia: '',
			descripcionProvincia: '',
			codigoMunicipio: '',
			descripcionMunicipio: '',
			codTipoVia: '',
			descripcionTipoVia: '',
			nombreVia: '',
			numeroVia: '',
			bloque: '',
			escalera: '',
			piso: '',
			puerta: '',
			codigoPostal: '',
			codigoPais: '',
			descripcionPais: '',
			codigoPostalExtranjero: '',
			estadoProvinciaExtranjero: '',
			municipioExtranjero: '',
			datosAmpliados: '',
		},
	};
};

export const defaultValuesRepresentante = () => {
	const { idTipoPersona, idTipoIntervencion, representantes, ...rest } = getDefaultValues({});
	return { ...rest, telefonoCodigoPais: ID_PAIS.ESPANA };
};

const codigoPostalSchema = (schema: yup.StringSchema) =>
	schema
		.test('cp-no-valido', ERROR_MESSAGE.codPostalNoValido, (value, context) => {
			if (!value) return true;
			const parent = context.parent as DireccionForm;
			return parent.codProvincia !== undefined && REGEX_NUMBER.test(value ?? '');
		})
		.test('min-5-char', ERROR_MESSAGE.codPostalMin, (value) => (value ? value.length >= 5 : true));

const docIdentSchema = (schema: yup.StringSchema) =>
	schema.test('doc-ident-no-valido', ERROR_MESSAGE.docIdentNoValidFormat, (value, context) => {
		const parent = context.parent as SujetoForm;
		if (!value) return true;

		if (parent.idTipoDocumentoIdent === String(TIPO_DOCUMENTO_IDENT.NIF)) {
			return nifValidation(value);
		}

		if (parent.idTipoDocumentoIdent === String(TIPO_DOCUMENTO_IDENT.NIE)) {
			return nieValidation(value);
		}

		if (parent.idTipoDocumentoIdent === String(TIPO_DOCUMENTO_IDENT.PASAPORTE)) {
			return value.length >= 5 && value.length <= 14;
		}
	});

const direccionSchema = yup.object().shape({
	idTipoDireccion: yup.string().required(ERROR_MESSAGE.required),

	codTipoVia: yup.string().when('idTipoDireccion', {
		is: String(TIPO_DIRECCION.NACIONAL),
		then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val)),
		otherwise: (schema) => schema.nullable(),
	}),

	codProvincia: yup.string().when('idTipoDireccion', {
		is: String(TIPO_DIRECCION.NACIONAL),
		then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val)),
		otherwise: (schema) => schema.nullable(),
	}),

	codigoMunicipio: yup.string().when('idTipoDireccion', {
		is: String(TIPO_DIRECCION.NACIONAL),
		then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val)),
		otherwise: (schema) => schema.nullable(),
	}),

	nombreVia: yup.string().when('idTipoDireccion', {
		is: (val: string) => val === String(TIPO_DIRECCION.NACIONAL) || val === String(TIPO_DIRECCION.EXTRANJERA),
		then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val)),
		otherwise: (schema) => schema.nullable(),
	}),

	numeroVia: yup.string().when('idTipoDireccion', {
		is: String(TIPO_DIRECCION.NACIONAL),
		then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val)),
		otherwise: (schema) => schema.nullable(),
	}),

	codigoPostal: yup.string().when('idTipoDireccion', {
		is: String(TIPO_DIRECCION.NACIONAL),
		then: (schema) => codigoPostalSchema(schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val))),
		otherwise: (schema) => codigoPostalSchema(schema),
	}),

	codigoPais: yup.string().when('idTipoDireccion', {
		is: String(TIPO_DIRECCION.EXTRANJERA),
		then: (schema) => schema.test(ERROR_TYPE.required, ERROR_MESSAGE.required, (val) => Boolean(val)),
		otherwise: (schema) => schema.nullable(),
	}),
});

const validateDuplicatedSubject = ({
	value,
	globalValues,
	idTipoIntervencion,
	id = 0,
	idReact = '0',
}: {
	value: string;
	globalValues: FormValues;
	idTipoIntervencion: number;
	id?: number;
	idReact?: string;
}) => {
	if (!value) return true;
	const sujetosWithSameIntervencion = getAllSujetos(globalValues)
		.filter((value) => idTipoIntervencion === value.tipoIntervencion.id)
		.filter((value) => value.id !== id && value.idReact !== idReact);

	const documentos = sujetosWithSameIntervencion.map((item) => item?.numeroDocumento).filter((doc) => doc === value);
	const currentDocumentos = [...documentos, value];
	return new Set(currentDocumentos).size === currentDocumentos.length;
};

const validateDuplicatedRepresentante = (value: IRepresentante[]) => {
	if (!value) return true;
	const documentos = value.map((item) => item?.numeroDocumento).filter((doc) => doc);
	return new Set(documentos).size === documentos.length;
};

export const schema = ({ globalValues }: { globalValues?: FormValues }) =>
	yup.object().shape({
		idTipoPersona: yup.string().required(ERROR_MESSAGE.required),
		idTipoDocumentoIdent: yup.string().required(ERROR_MESSAGE.required),
		idTipoIntervencion: yup.string().required(ERROR_MESSAGE.required),
		representantes: yup.array().when('idTipoPersona', {
			is: (val: string) => val === String(TIPO_PERSONA.JURIDICA),
			then: (schema) =>
				schema
					.required(ERROR_MESSAGE.intervinienteConRepresentante)
					.min(1, ERROR_MESSAGE.intervinienteConRepresentante)
					.test(
						ERROR_TYPE.duplicatedRepresentante,
						ERROR_MESSAGE.duplicatedRepresentante,
						validateDuplicatedRepresentante,
					),
			otherwise: (schema) => schema.nullable(),
		}),
		nacionalidad: yup.string().when('idTipoDireccion', {
			is: (val: string) => val === String(TIPO_DOCUMENTO_IDENT.PASAPORTE),
			then: (schema) => schema.required(ERROR_MESSAGE.nacionalidadRequired),
			otherwise: (schema) => schema.nullable(),
		}),
		numeroDocumento: yup
			.string()
			.required(ERROR_MESSAGE.required)
			.when('idTipoPersona', {
				is: (val: string) => val === String(TIPO_PERSONA.JURIDICA),
				then: (schema) =>
					schema.test('doc-ident-no-valido', ERROR_MESSAGE.docIdentNoValidFormat, (value) =>
						cifValidation(value),
					),
				otherwise: (schema) => docIdentSchema(schema),
			})
			.when('idTipoPersona', {
				is: (val: string) => val === String(TIPO_PERSONA.JURIDICA),
				then: (schema) =>
					schema.test(ERROR_TYPE.duplicatedJuridico, ERROR_MESSAGE.docIdentNoValid, (value, context) => {
						if (!globalValues) return true;
						const { idTipoIntervencion, id, idReact } = context.parent as SujetoForm;
						return validateDuplicatedSubject({
							value,
							globalValues,
							idTipoIntervencion: Number(idTipoIntervencion),
							id,
							idReact,
						});
					}),
				otherwise: (schema) => schema,
			})
			.when('idTipoPersona', {
				is: (val: string) => val === String(TIPO_PERSONA.FISICA),
				then: (schema) =>
					schema.test(ERROR_TYPE.duplicatedFisica, ERROR_MESSAGE.docIdentNoValid, (value, context) => {
						if (!globalValues) return true;
						const { idTipoIntervencion, id, idReact } = context.parent as SujetoForm;
						return validateDuplicatedSubject({
							value,
							globalValues,
							idTipoIntervencion: Number(idTipoIntervencion),
							id,
							idReact,
						});
					}),
				otherwise: (schema) => schema,
			}),
		nombre: yup.string().when('idTipoPersona', {
			is: (val: string) => val === String(TIPO_PERSONA.FISICA),
			then: (schema) => schema.required(ERROR_MESSAGE.required),
			otherwise: (schema) => schema.nullable(),
		}),
		apellido1RacSoc: yup.string().required(ERROR_MESSAGE.required),
		email: yup
			.string()
			.when('idTipoPersona', {
				is: (val: string) => val === String(TIPO_PERSONA.FISICA),
				then: (schema) => 
					schema
						.email(ERROR_MESSAGE.emailNoValido)
						.required(ERROR_MESSAGE.required),
				otherwise: (schema) => 
					schema
						.nullable()
						.notRequired(),
			}),
		telefonoCodigoPais: yup.string().when('idTipoPersona', {
			is: (val: string) => val === String(TIPO_PERSONA.FISICA),
			then: (schema) => schema.required(ERROR_MESSAGE.required),
			otherwise: (schema) => schema.nullable(),
		}),
		telefonoNumero: yup.string().when('idTipoPersona', {
			is: (val: string) => val === String(TIPO_PERSONA.FISICA),
			then: (schema) =>
				schema
					.required(ERROR_MESSAGE.required)
					.test('telefono-no-valido', ERROR_MESSAGE.telefonoNoValido, (value) =>
						REGEX_NUMBER.test(value ?? ''),
					),
			otherwise: (schema) => schema.nullable(),
		}),
		direccion: direccionSchema,
	});
	
export const resolver =
    ({ globalValues }: { globalValues?: FormValues }): Resolver<any, string> =>
    	async (values, context, options) => {
    		const schemaDefined = schema({
    			globalValues,
    		});
    		return yupResolver(schemaDefined)(values, context, options);
    	};

export const resolverRepresentante = ({
	maxSizeAllDoc,
	globalValues,
}: {
	maxSizeAllDoc: number;
	globalValues?: FormValues;
}): Resolver<any, string> =>
	yupResolver(
		yup.object().shape({
			idTipoDocumentoIdent: yup.string().required(ERROR_MESSAGE.required),
			nacionalidad: yup.string().when('idTipoDocumentoIdent', {
				is: (val: string) => val === String(TIPO_DOCUMENTO_IDENT.PASAPORTE),
				then: (schema) => schema.required(ERROR_MESSAGE.nacionalidadRequired),
				otherwise: (schema) => schema.nullable(),
			}),
			numeroDocumento: docIdentSchema(yup.string().required(ERROR_MESSAGE.required)),
			nombre: yup.string().required(ERROR_MESSAGE.required),
			apellido1RacSoc: yup.string().required(ERROR_MESSAGE.required),
			email: yup.string().email(ERROR_MESSAGE.emailNoValido).required(ERROR_MESSAGE.required),
			telefonoCodigoPais: yup.string().required(ERROR_MESSAGE.required),
			telefonoNumero: yup
				.string()
				.required(ERROR_MESSAGE.required)
				.test('telefono-no-valido', ERROR_MESSAGE.telefonoNoValido, (value) => REGEX_NUMBER.test(value ?? '')),
			documentoPoder: documentoLimitadoSchema({ globalValues, maxSizeAllDoc })
				.required(ERROR_MESSAGE.required)
				.test('is-required', ERROR_MESSAGE.required, (value: any) =>
					Boolean(value?.file || value?.id || value?.nombreArchivo || value?.nombre),
				),
			direccion: direccionSchema,
		}),
	);